import { useState } from "react";
import { Camera, Shield, Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useChangePassword } from "@/hooks/api/use-auth";
import { useToast } from "@/hooks/use-toast";

const AdminProfile = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const changePassword = useChangePassword();
  const { toast } = useToast();
  
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    password: "",
    password_confirmation: "",
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirmation: false,
  });

  const handlePasswordChange = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (passwordData.password !== passwordData.password_confirmation) {
      toast({
        title: t('auth.passwordMismatch') || 'خطأ في كلمة المرور',
        description: t('auth.passwordsDoNotMatch') || 'كلمة المرور الجديدة وتأكيد كلمة المرور غير متطابقين',
        variant: 'destructive',
      });
      return;
    }

    if (passwordData.password.length < 8) {
      toast({
        title: t('auth.passwordTooShort') || 'كلمة المرور قصيرة',
        description: t('auth.passwordMinLength') || 'يجب أن تكون كلمة المرور 8 أحرف على الأقل',
        variant: 'destructive',
      });
      return;
    }

    changePassword.mutate(passwordData);
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirmation') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  if (!user) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <Skeleton className="h-8 w-32 mx-auto mb-4" />
          <Skeleton className="h-4 w-48 mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-foreground">{t('profile.title') || 'الملف الشخصي'}</h1>
        <p className="text-muted-foreground mt-1">{t('profile.subtitle') || 'إدارة معلومات حسابك'}</p>
      </div>

      {/* Profile Information Card */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            {t('profile.basicInfo') || 'المعلومات الأساسية'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('profile.name') || 'الاسم'}</p>
                <p className="text-foreground font-semibold">{user.name || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('profile.email') || 'البريد الإلكتروني'}</p>
                <p className="text-foreground font-semibold">{user.email || '-'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('profile.role') || 'الدور'}</p>
                <p className="text-foreground font-semibold">
                  {user.type === 'admin' ? (t('profile.admin') || 'مدير') : (t('profile.client') || 'عميل')}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('profile.status') || 'الحالة'}</p>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  {t('profile.active') || 'نشط'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground">{t('profile.memberSince') || 'عضو منذ'}</p>
                <p className="text-foreground font-semibold">
                  {user.created_at ? new Date(user.created_at).toLocaleDateString('ar-EG') : '-'}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card className="shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            {t('profile.changePassword') || 'تغيير كلمة المرور'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordChange} className="space-y-6">
            <div className="flex justify-center mb-6">
              <div className="relative">
                <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center text-primary-foreground text-3xl font-bold">
                  {user.name?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="absolute bottom-0 right-0 w-8 h-8 bg-secondary rounded-full flex items-center justify-center text-secondary-foreground">
                  <Camera size={16} />
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t('profile.currentPassword') || 'كلمة المرور الحالية'}
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.current ? "text" : "password"}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                    disabled={changePassword.isPending}
                    placeholder={t('profile.enterCurrentPassword') || 'أدخل كلمة المرور الحالية'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                    onClick={() => togglePasswordVisibility('current')}
                  >
                    {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t('profile.newPassword') || 'كلمة المرور الجديدة'}
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.new ? "text" : "password"}
                    value={passwordData.password}
                    onChange={(e) => setPasswordData({ ...passwordData, password: e.target.value })}
                    disabled={changePassword.isPending}
                    placeholder={t('profile.enterNewPassword') || 'أدخل كلمة المرور الجديدة'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                    onClick={() => togglePasswordVisibility('new')}
                  >
                    {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  {t('profile.confirmNewPassword') || 'تأكيد كلمة المرور الجديدة'}
                </label>
                <div className="relative">
                  <Input
                    type={showPasswords.confirmation ? "text" : "password"}
                    value={passwordData.password_confirmation}
                    onChange={(e) => setPasswordData({ ...passwordData, password_confirmation: e.target.value })}
                    disabled={changePassword.isPending}
                    placeholder={t('profile.confirmNewPassword') || 'أكد كلمة المرور الجديدة'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8"
                    onClick={() => togglePasswordVisibility('confirmation')}
                  >
                    {showPasswords.confirmation ? <EyeOff size={16} /> : <Eye size={16} />}
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <Button type="submit" size="lg" disabled={changePassword.isPending}>
                {changePassword.isPending 
                  ? (t('common.loading') || 'جاري الحفظ...') 
                  : (t('profile.changePassword') || 'تغيير كلمة المرور')
                }
              </Button>
              <Button
                type="button"
                variant="outline"
                size="lg"
                onClick={() => setPasswordData({
                  current_password: "",
                  password: "",
                  password_confirmation: "",
                })}
                disabled={changePassword.isPending}
              >
                {t('common.cancel') || 'إلغاء'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminProfile;
